import { useState, useEffect } from 'react';
import { Star, Send, CheckCircle, Loader2 } from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { apiService } from '../../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedbackForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [programs, setPrograms] = useState([]);

  // Form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [experienceQuality, setExperienceQuality] = useState('');
  const [recommendLikelihood, setRecommendLikelihood] = useState([7]);
  const [whatYouLike, setWhatYouLike] = useState('');
  const [suggestions, setSuggestions] = useState('');
  const [program, setProgram] = useState('');

  // Load programs for dropdown
  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const data = await apiService.getPrograms();
        if (data) {
          setPrograms(data);
        }
      } catch (err) {
        console.error('Failed to load programs:', err);
      }
    };
    loadPrograms();
  }, []);

  const getNPSLabel = (value) => {
    if (value >= 9) return 'Promoter';
    if (value >= 7) return 'Passive';
    return 'Detractor';
  };

  const getNPSColor = (value) => {
    if (value >= 9) return 'text-green-600';
    if (value >= 7) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (rating === 0) {
      setError('Please select a star rating');
      return;
    }
    if (!experienceQuality) {
      setError('Please select your experience quality');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rating,
          experience_quality: experienceQuality,
          recommend_likelihood: recommendLikelihood[0],
          what_you_like: whatYouLike || null,
          suggestions: suggestions || null,
          program: program || null,
          source: 'qr_code',
        }),
      });

      if (response.status === 429) {
        setError('Too many submissions. Please try again later.');
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit feedback');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Thank You for Your Feedback!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your feedback helps us improve and provide the best martial arts
              training experience for our students and families.
            </p>
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
              <p className="font-semibold text-gray-700 dark:text-gray-300">Owaza Martial Arts</p>
              <p>14726 County Line Rd, Spring Hill, FL 34610</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4">
      <Card className="max-w-xl mx-auto">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-2xl">Share Your Feedback</CardTitle>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Help us improve by sharing your experience at Owaza Martial Arts
          </p>
        </CardHeader>

        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Star Rating */}
            <div>
              <Label className="text-base font-medium">
                Overall Satisfaction <span className="text-red-500">*</span>
              </Label>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">How would you rate your experience?</p>
              <div className="flex gap-2 justify-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-10 h-10 ${
                        star <= (hoverRating || rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300 dark:text-gray-600'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-2">
                  {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
                </p>
              )}
            </div>

            {/* Experience Quality */}
            <div>
              <Label className="text-base font-medium">
                How was your experience? <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-3 mt-3">
                {['excellent', 'good', 'average', 'poor'].map((quality) => (
                  <button
                    key={quality}
                    type="button"
                    onClick={() => setExperienceQuality(quality)}
                    className={`p-3 rounded-lg border-2 transition-all capitalize ${
                      experienceQuality === quality
                        ? 'border-red-600 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    {quality}
                  </button>
                ))}
              </div>
            </div>

            {/* NPS Slider */}
            <div>
              <Label className="text-base font-medium">
                How likely are you to recommend Owaza to a friend?
              </Label>
              <p className="text-sm text-gray-500 mb-4">0 = Not likely, 10 = Extremely likely</p>
              <div className="px-2">
                <Slider
                  value={recommendLikelihood}
                  onValueChange={setRecommendLikelihood}
                  max={10}
                  min={0}
                  step={1}
                  className="mb-2"
                />
                <div className="flex justify-between text-sm text-gray-500">
                  <span>0</span>
                  <span className={`font-medium ${getNPSColor(recommendLikelihood[0])}`}>
                    {recommendLikelihood[0]} - {getNPSLabel(recommendLikelihood[0])}
                  </span>
                  <span>10</span>
                </div>
              </div>
            </div>

            {/* Program Dropdown (Optional) */}
            <div>
              <Label className="text-base font-medium">Which program are you in?</Label>
              <p className="text-sm text-gray-500 mb-2">Optional</p>
              <Select value={program} onValueChange={setProgram}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a program (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_specified">Prefer not to say</SelectItem>
                  {programs.map((p) => (
                    <SelectItem key={p.id} value={p.title}>
                      {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* What You Like */}
            <div>
              <Label className="text-base font-medium">What do you like most?</Label>
              <p className="text-sm text-gray-500 mb-2">Optional</p>
              <Textarea
                value={whatYouLike}
                onChange={(e) => setWhatYouLike(e.target.value)}
                placeholder="Tell us what you enjoy about training at Owaza..."
                className="min-h-[80px]"
                maxLength={500}
              />
            </div>

            {/* Suggestions */}
            <div>
              <Label className="text-base font-medium">Any suggestions for improvement?</Label>
              <p className="text-sm text-gray-500 mb-2">Optional</p>
              <Textarea
                value={suggestions}
                onChange={(e) => setSuggestions(e.target.value)}
                placeholder="We're always looking to improve..."
                className="min-h-[80px]"
                maxLength={500}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-red-600 hover:bg-red-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Feedback
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackForm;
